import React, { useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Box, Breadcrumbs, CircularProgress, Fab, Grid, Link, Typography } from "@mui/material";
import { AddCircle as AddCircleIcon } from "@mui/icons-material";
import MediaViewer from "./MediaViewer";
import VisitHeader from "./VisitHeader";
import AddStudyDialog from "./AddStudyDialog";
import { IPatient } from "../patients/patientsSlice";
import { IStudy, UserCRF } from "../studies/studiesSlice";
import { useGetPatientVisitsQuery } from "../studies/dataAccess";
import { useGetPatientByIdQuery } from "../patients/dataAccess";
import { getMediaViewerIdsFromUri, getQueryParamFromStudyId } from "./utils";
import * as queryString from "query-string";
import CRFDrawer from "./CRFDrawer";

const VisitsAnalyzer = () => {
    const { patientId } = useParams<{ patientId: string }>();
    const location = useLocation();
    const history = useHistory();
    const [addStudyDialogOpen, setAddStudyDialogOpen] = useState(false);

    const selectedMediaViewerIds = getMediaViewerIdsFromUri(location.search) as string[];
    const [selectedStudy, setSelectedStudy] = useState<IStudy>();

    const { isLoading: studyLoading, data } = useGetPatientVisitsQuery(patientId);

    const { data: patientData } = useGetPatientByIdQuery(patientId);

    const patient = patientData;

    const handleOpenAddStudyDialog = () => {
        setAddStudyDialogOpen(true);
    };

    const handleCloseAddStudyDialog = () => {
        setAddStudyDialogOpen(false);
    };

    const handleAddStudy = (newStudyId: string) => {
        const studiesIds = getMediaViewerIdsFromUri(location.search) as string[];

        const studyIdAsQueryParam = getQueryParamFromStudyId(newStudyId);

        history.push({
            pathname: location.pathname,
            search: queryString.stringify({ st: [...studiesIds, studyIdAsQueryParam] }),
        });
        handleCloseAddStudyDialog();
    };

    const handleCloseDrawer = () => setSelectedStudy(undefined);

    const handleOpenDrawer = (selectedStudy: IStudy) => {
        setSelectedStudy(selectedStudy);
    };

    if (studyLoading || !patient)
        return (
            <Box sx={{ height: "calc(100% - 65px)", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
            </Box>
        );

    return (
        <Box sx={{ height: "calc(100% - 65px)", display: "flex", flexDirection: "column" }}>
            <VisitHeader patient={patient as IPatient} />
            <Box
                sx={{
                    padding: 20,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "auto",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 8 }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" href="/patients">
                            Patients
                        </Link>
                        <Typography color="text.primary">{patient!.patientName}</Typography>
                    </Breadcrumbs>
                    <Fab
                        sx={{ backgroundColor: "white" }}
                        variant="extended"
                        size="small"
                        color="primary"
                        aria-label="add"
                        onClick={handleOpenAddStudyDialog}
                    >
                        <AddCircleIcon sx={{ mr: 8 }} />
                        Add Study
                    </Fab>
                </Box>
                {selectedMediaViewerIds.length === 0 ? (
                    <Box sx={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center" }}>
                        Select a study
                    </Box>
                ) : (
                    <Grid container spacing={16}>
                        {selectedMediaViewerIds.map((mediaViewerId: string) => {
                            const key = mediaViewerId.split(":")[0];
                            return (
                                <Grid
                                    sx={{
                                        my: 8,
                                    }}
                                    item
                                    xl={6}
                                    xs={12}
                                    key={key}
                                >
                                    <MediaViewer onOpenCRF={handleOpenDrawer} mediaViewerId={mediaViewerId} />
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
            <CRFDrawer
                selectedVisitId={selectedStudy?.imagingProcedure.visitUuid || ""}
                isOpen={Boolean(selectedStudy)}
                title={`CRF for: ${selectedStudy?.imagingProcedure.visit}`}
                onClose={handleCloseDrawer}
                patient={patientData}
            />
            <AddStudyDialog
                onClose={handleCloseAddStudyDialog}
                onAddStudy={handleAddStudy}
                open={addStudyDialogOpen}
                studies={data?.studies as IStudy[]}
            />
        </Box>
    );
};

export default VisitsAnalyzer;
